import type { Paragraph } from '@/api/types/stowed-element'
import { countRunCharacters } from './helpers/backward-pull-policy'
import { cloneParagraphsDeep } from '@/lib/text/text-style-engine'

/**
 * Removes exactly the last plain-text character from `paragraphs`, mirroring what a native
 * Backspace would do at the very end of this content — used to pull one character back across a
 * linked-text-chain frame boundary (see `handleChainBackspaceAtStart` in use-home-chain-editing.ts).
 *
 * **Counts `isHidden` lines, and deletes out of them.** That is the shape-text character convention
 * — the one `paragraphsToPlainText` (`lib/shapes/shape-text.ts`),
 * `measureShapeParagraphsPlainTextLength`, `splitShapeTextParagraphsAtOffset` and the editor's own
 * `textValue` all share — and this function must agree with it, because its caller feeds
 * `measureShapeParagraphsPlainTextLength` of this result straight to `setSelectionRange` via
 * `enterChainFrameEditAtOffset`.
 *
 * It used to measure with `paragraphPlainText` (`lib/text/paragraph-plain-text.ts`) and skip hidden
 * lines outright. Those two conventions disagree exactly when a frame is carrying **overset** — text
 * past what the shape lays out, which `use-shape-text-editing.ts` now accepts rather than blocking
 * (WMPCM-9338) and `bakeShapeTextWireLines` persists as `isHidden` wire lines, so it survives a
 * reload. On such a frame the old code deleted the last VISIBLE character, i.e. one from the middle
 * of the story rather than the one before the caret, and the caller then placed the caret past the
 * visible end because its own measurement counted the hidden tail — QA's "the caret is placed before
 * the last letter and that last letter is hidden" (WMPCM-9070, 2026-08-18 Bug 1). A last paragraph
 * made only of hidden lines was worse: it read as empty and the whole paragraph was dropped, losing
 * every overset character in it.
 *
 * A wholly empty trailing paragraph (renders only via the nbsp placeholder — see
 * `appendParagraphRunSpans` in `content-editable-paragraph-blocks.ts`) is dropped entirely rather
 * than "trimmed", mirroring how backspace at the start of an empty last line merges it into the
 * previous line. No-ops (returns an equivalent clone) when `paragraphs` is empty or is already a
 * single, wholly empty paragraph — there is nothing left to pull back.
 */
export function deleteTrailingCharacter(paragraphs: Paragraph[]): Paragraph[] {
  const clone = cloneParagraphsDeep(paragraphs)

  if (clone.length === 0) return clone

  const lastIndex = clone.length - 1
  const lastParagraph = clone[lastIndex]!

  if (countRunCharacters([lastParagraph]) === 0) {
    if (clone.length === 1) return clone

    clone.splice(lastIndex, 1)

    return clone
  }

  const lines = lastParagraph.textLines ?? []

  for (let lineIdx = lines.length - 1; lineIdx >= 0; lineIdx--) {
    const line = lines[lineIdx]!
    const runs = line.textRuns ?? []

    for (let runIdx = runs.length - 1; runIdx >= 0; runIdx--) {
      const run = runs[runIdx]!
      const text = run.textContent ?? ''

      if (text.length === 0) continue

      const nextText = text.slice(0, -1)

      if (nextText.length === 0) {
        runs.splice(runIdx, 1)
      } else {
        run.textContent = nextText
      }

      return clone
    }
  }

  return clone
}
